/**
 * Recipe Show Page
 *
 * Displays full recipe details including:
 * - Header with image, actions, title, description, meta grid, tags
 * - Two-column layout with ingredients/instructions and sidebar
 * - Family ratings, storage info, and prep history in sidebar
 * - Interactive rating add/edit and prep logging via modals
 */

import { useCallback } from 'react';
import { Head, Link, router } from '@inertiajs/react';
import type { RecipeShowProps } from '@/types/food';
import { getTagStyle } from '@/types/food';
import { Navbar } from '@/components';
import { IngredientList } from '@/components/food/IngredientList';
import { FamilyRatings } from '@/components/food/FamilyRatings';
import { StorageInfo } from '@/components/food/StorageInfo';
import { PrepHistory } from '@/components/food/PrepHistory';

// SVG Icons
function ClockIcon({ className = 'w-5 h-5' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 6v6l4 2" />
    </svg>
  );
}

function PanIcon({ className = 'w-5 h-5' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <circle cx="12" cy="12" r="8" />
      <path d="M12 8v4l2 2" />
      <line x1="20" y1="12" x2="22" y2="12" />
    </svg>
  );
}

function ServingsIcon({ className = 'w-5 h-5' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function FireIcon({ className = 'w-5 h-5' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <path d="M12 2c.5 3.5-1 6-3 8 1.5 1 3 1.5 3.5 4 .5-2.5 2-3 3.5-4-2-2-3.5-4.5-3-8z" />
      <path d="M12 12c.5 2-1 3-2 4 1 .5 2 1 2 2.5.5-1.5 1.5-2 2.5-2.5-1-1-2-2.5-1.5-4z" />
    </svg>
  );
}

function PencilIcon({ className = 'w-4 h-4' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  );
}

function CopyIcon({ className = 'w-4 h-4' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}

function TrashIcon({ className = 'w-4 h-4' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <path d="M3 6h18" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </svg>
  );
}

function parseInstructions(instructions: string | null | undefined): string[] {
  if (!instructions) return [];

  // Split by numbered patterns like "1." or "1)" or just newlines
  const lines = instructions.split(/\n/).filter((line) => line.trim());

  return lines.map((line) => {
    // Remove leading numbers and punctuation
    return line.replace(/^\d+[\.\)]\s*/, '').trim();
  });
}

export default function Show({ recipe, family_members }: RecipeShowProps) {
  // Refresh page data after rating/prep mutations
  const handleDataChange = useCallback(() => {
    router.reload({ only: ['recipe'] });
  }, []);

  if (!recipe) {
    return <div className="container mx-auto px-4 py-8">Recipe not found</div>;
  }

  const instructionSteps = parseInstructions(recipe.instructions);

  const handleDelete = () => {
    if (confirm('Are you sure you want to delete this recipe?')) {
      router.delete(`/food/recipes/${recipe.id}`);
    }
  };

  const handleDuplicate = () => {
    router.post(`/food/recipes/${recipe.id}/duplicate`);
  };

  return (
    <>
      <Head title={recipe.title} />
      <div className="min-h-screen bg-gray-50">
        <Navbar currentSection="food" />
        <div className="max-w-5xl mx-auto px-5 py-5">
          {/* Breadcrumb */}
        <nav className="flex items-center gap-2 mb-5 text-sm">
          <Link href="/food/recipes" className="text-blue-600 hover:underline">
            Recipes
          </Link>
          <span className="text-gray-400">/</span>
          <span className="text-gray-500">{recipe.title}</span>
        </nav>

        {/* Header */}
        <div className="grid grid-cols-1 md:grid-cols-[1fr_400px] gap-8 bg-white rounded-2xl overflow-hidden shadow-sm mb-6">
          {/* Image */}
          <div className="h-72 md:h-[350px] bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
            {recipe.photoUrl ? (
              <img
                src={recipe.photoUrl}
                alt={recipe.title}
                className="w-full h-full object-cover"
              />
            ) : (
              <svg
                className="w-24 h-24 text-white/50"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.5}
              >
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <path d="m21 15-5-5L5 21" />
              </svg>
            )}
          </div>

          {/* Info */}
          <div className="p-6 flex flex-col">
            {/* Actions */}
            <div className="flex gap-2 mb-4">
              <Link
                href={`/food/recipes/${recipe.id}/edit`}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
              >
                <PencilIcon />
                Edit Recipe
              </Link>
              <button
                type="button"
                onClick={handleDuplicate}
                className="inline-flex items-center gap-1.5 px-4 py-2 border border-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
              >
                <CopyIcon />
                Duplicate
              </button>
              <button
                type="button"
                onClick={handleDelete}
                className="inline-flex items-center gap-1.5 px-4 py-2 border border-gray-200 text-red-600 text-sm font-medium rounded-lg hover:bg-red-50 hover:border-red-200 transition-colors"
              >
                <TrashIcon />
                Delete
              </button>
            </div>

            {/* Title & Description */}
            <h1 className="text-2xl font-bold text-gray-900 mb-3">{recipe.title}</h1>
            {recipe.description && (
              <p className="text-gray-500 mb-4 flex-1">{recipe.description}</p>
            )}

            {/* Meta Grid */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-500">
                  <ClockIcon />
                </span>
                <div className="flex flex-col">
                  <span className="text-xs text-gray-400 uppercase tracking-wide">
                    Prep Time
                  </span>
                  <span className="font-semibold text-gray-900">
                    {recipe.preparationTimeMinutes
                      ? `${recipe.preparationTimeMinutes} min`
                      : 'N/A'}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-500">
                  <PanIcon />
                </span>
                <div className="flex flex-col">
                  <span className="text-xs text-gray-400 uppercase tracking-wide">
                    Cook Time
                  </span>
                  <span className="font-semibold text-gray-900">
                    {recipe.cookingTimeMinutes
                      ? `${recipe.cookingTimeMinutes} min`
                      : 'N/A'}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-500">
                  <ServingsIcon />
                </span>
                <div className="flex flex-col">
                  <span className="text-xs text-gray-400 uppercase tracking-wide">
                    Servings
                  </span>
                  <span className="font-semibold text-gray-900">
                    {recipe.servings ? `${recipe.servings} portions` : 'N/A'}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <span className="text-orange-500">
                  <FireIcon />
                </span>
                <div className="flex flex-col">
                  <span className="text-xs text-gray-400 uppercase tracking-wide">
                    Calories
                  </span>
                  <span className="font-semibold text-gray-900">
                    {recipe.caloriesPerPortion
                      ? `${recipe.caloriesPerPortion} kcal/serving`
                      : 'N/A'}
                  </span>
                </div>
              </div>
            </div>

            {/* Tags */}
            {recipe.tags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {recipe.tags.map((tag) => {
                  const style = getTagStyle(tag.namespace);
                  return (
                    <span
                      key={tag.id}
                      className={`px-3 py-1 rounded-full text-xs font-medium ${style.bg} ${style.text}`}
                    >
                      {tag.name}
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
          {/* Main Content */}
          <div className="flex flex-col gap-6">
            {/* Ingredients Card */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Ingredients</h2>
                <span className="text-sm text-gray-500">
                  {recipe.ingredients.length} items
                </span>
              </div>
              <IngredientList ingredients={recipe.ingredients} />
            </div>

            {/* Instructions Card */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Instructions</h2>
              </div>
              {instructionSteps.length > 0 ? (
                <ol className="list-none">
                  {instructionSteps.map((step, index) => (
                    <li
                      key={index}
                      className="flex gap-4 py-4 border-b border-gray-100 last:border-b-0"
                    >
                      <span className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-semibold text-sm flex-shrink-0">
                        {index + 1}
                      </span>
                      <div className="flex-1 pt-1 text-gray-700">{step}</div>
                    </li>
                  ))}
                </ol>
              ) : (
                <p className="text-gray-500">No instructions provided.</p>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="flex flex-col gap-6">
            {/* Family Ratings Card */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Family Ratings</h2>
                <span className="text-sm text-gray-500">
                  {recipe.averageRating != null
                    ? `Avg: ${recipe.averageRating.toFixed(1)}`
                    : 'No ratings'}
                </span>
              </div>
              <FamilyRatings
                ratings={recipe.ratings}
                familyMembers={family_members}
                recipeId={recipe.id}
                onRatingsChange={handleDataChange}
              />
            </div>

            {/* Storage Card */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Storage</h2>
              </div>
              <StorageInfo
                eatImmediately={recipe.eatImmediately}
                fridgeLifeDays={recipe.fridgeLifeDays}
                freezable={recipe.freezable}
                healthinessRating={recipe.healthinessRating}
              />
            </div>

            {/* Prep History Card */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Prep History</h2>
              </div>
              <PrepHistory
                prepHistory={recipe.prepHistory}
                recipeId={recipe.id}
                defaultServings={recipe.servings}
                onPrepsChange={handleDataChange}
              />
            </div>
          </div>
        </div>
        </div>
      </div>
    </>
  );
}
